import { ProjectPopover } from "@/components/popovers/project-popover";
import { ProjectsFilter, useProjectsFilterController } from "@/components/projects";
import { client } from "@/hono";
import { useMembers } from "@/hooks/use-members";
import { useProjects } from "@/hooks/use-projects";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { ContentBox } from "@msuite/ui/content-box";
import { ContentCard } from "@msuite/ui/content-card";
import { FormCheckbox, TextInput } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { MapView, MapViewMarker, type Marker } from "@msuite/ui/map";
import { Popover } from "@msuite/ui/popover";
import { useQuery } from "@msuite/ui/query";
import { getGeometryCoordinates } from "@msuite/utils/misc";
import { generateId, guardString } from "@msuite/utils/strings";
import { useMemo, useState } from "react";
import { useMapViewHandle } from "@msuite/ui/map";
import { useForm, useWatch } from "react-hook-form";
import { responseHandler } from "@msuite/ui/utils";

function createMarker(latitude: number, longitude: number): Marker & { id: string } {
	return {
		id: generateId(),
		latitude,
		longitude,
		excludeFromClustering: true,
		render: () => (
			<div className="w-6 aspect-square relative z-100 pointer-events-none">
				<div className="w-full h-full rounded-full bg-accent-500">
					<div className="rounded-full bg-accent-500 animate-ping absolute inset-0" />
					<div className="rounded-full border-2 border-white absolute inset-0" />
				</div>
			</div>
		),
	};
}

export function ProjectMap() {
	/** Form */
	const methods = useForm({
		defaultValues: {
			isNearbySearch: false,
		},
	});
	const [isNearbySearch] = useWatch({ control: methods.control, name: ["isNearbySearch"] });

	/** State */
	const [addressMarker, setAddressMarker] = useState<(Marker & { id: string }) | null>(null);

	/** Hooks */
	const { t } = useTranslation();
	const { projects } = useProjects();
	const { members } = useMembers();
	const { workspaces } = useWorkspaces();
	const projectsFilter = useProjectsFilterController(projects, {
		users: members,
		workspaces,
		isDisabled: isNearbySearch,
	});
	const mapViewHandle = useMapViewHandle();
	const hasAddressSearch = isNearbySearch && !!projectsFilter.searchValue;

	/** Memoized */
	const markers = useMemo(() => {
		const markers: Marker[] = projectsFilter.filtered.flatMap((project) => {
			const { latitude, longitude } = getGeometryCoordinates(project.geometry);
			if (latitude === null || longitude === null) return [];
			return [
				{
					id: project.id,
					latitude,
					longitude,
					render: () => (
						<div>
							<Popover
								trigger="click"
								variant="plain"
								render={() => <ProjectPopover projectId={project.id} />}
							>
								<MapViewMarker
									iconName="FolderOpen"
									color="var(--color-red-500)"
								/>
							</Popover>
						</div>
					),
				},
			];
		});

		if (addressMarker && hasAddressSearch) {
			markers.push(addressMarker);
		}

		return markers;
	}, [projectsFilter.filtered, addressMarker, hasAddressSearch]);

	/** Query */
	useQuery({
		queryKey: ["nearby-search", guardString(projectsFilter.searchValue)],
		queryFn: async () => {
			if (!projectsFilter.searchValue) throw new Error();
			const response = await client.addresses.coordinates.address.$get({
				query: {
					address: guardString(projectsFilter.searchValue),
				},
			});
			const data = await responseHandler(response);
			mapViewHandle.current?.panTo({
				lat: data.coordinates.latitude,
				lng: data.coordinates.longitude,
			});
			mapViewHandle.current?.setZoom(17);
			setAddressMarker(createMarker(data.coordinates.latitude, data.coordinates.longitude));
			return data.coordinates;
		},
		enabled: !!projectsFilter.searchValue.length && isNearbySearch,
		staleTime: 1_000 * 60 * 10,
	});

	/** Render */
	return (
		<ContentBox>
			<div className="h-stack justify-between">
				<div className="h-stack gap-4">
					<div className="w-[400px]">
						<TextInput
							control={projectsFilter.searchFormControl}
							name="searchValue"
							type="search"
							placeholder={t("placeholders.search")}
							isRequired={false}
							label={undefined}
							debounceMs={2_000}
						/>
					</div>
					<FormCheckbox
						control={methods.control}
						name="isNearbySearch"
					>
						{t("projects.nearby_search")}
					</FormCheckbox>
				</div>
				<ProjectsFilter controller={projectsFilter} />
			</div>
			<ContentCard
				title={`${t("projects.project")} (${projectsFilter.count})`}
				icon={icon("Navigation")}
				className="h-full"
				contentClassName="h-full !p-0"
				isCollapsible={false}
			>
				<MapView
					apiKey={guardString(process.env.PUBLIC_GOOGLE_MAPS_API_KEY)}
					mapId={process.env.PUBLIC_GOOGLE_MAPS_MAP_ID!}
					markers={markers}
					useDefaultCenter
					ref={mapViewHandle}
				/>
			</ContentCard>
		</ContentBox>
	);
}
